import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Button,
  IconButton,
  Chip,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CalendarToday as CalendarTodayIcon,
  ArrowForward as ArrowForwardIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import UndoIcon from '@mui/icons-material/Undo';
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  isToday, 
  isSameDay,
  parseISO,
  getYear,
  getMonth
} from 'date-fns';
import { getSales } from '../firebase/financeServices';
import { getDailyClosingsForMonth } from '../firebase/dailyClosingServices';
import { reverseDailyClosing } from '../services/glDailyClosingService';
import ReverseGLEntryDialog from '../components/ReverseGLEntryDialog';
import { Timestamp } from 'firebase/firestore';
// Commission GL entry functionality has been moved to the Commission Breakdown page

// Define our own Sale interface that matches what we need
interface Sale {
  id: string;
  createdAt: Timestamp | Date;
  subtotal?: number;
  discount?: number;
  total: number;
  paymentMethod: string;
  items: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    total: number;
    type: string;
  }[];
  status?: string;
}

// Daily summary interface
interface DailySummary {
  date: Date;
  totalSales: number;
  transactionCount: number;
  voidedCount?: number;
  categories: {[key: string]: number};
  paymentMethods: {[key: string]: number};
  isClosed: boolean;
  notes?: string;
  glTransactionId?: string;
}

// Monthly totals interface
interface MonthlyTotals {
  totalSales: number;
  categories: {[key: string]: number};
  paymentMethods: {[key: string]: number};
}

// Helper function to format currency with RM
const formatCurrency = (amount: number): string => {
  return `RM ${amount.toFixed(2)}`;
};

// Row component for each day
interface DayRowProps {
  daySummary: DailySummary;
  onViewDetails: (date: Date) => void;
}

const DayRow: React.FC<DayRowProps> = ({ daySummary, onViewDetails }) => {
  const [isReversingClosing, setIsReversingClosing] = useState<boolean>(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  
  // Open the reverse closing dialog
  const handleOpenReverseDialog = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row expansion
    if (!daySummary.glTransactionId) return;
    setReverseDialogOpen(true);
  };
  
  // Handle reversing daily closing
  const handleReverseClosing = async () => {
    setIsReversingClosing(true);
    
    try {
      await reverseDailyClosing(daySummary.date);
      enqueueSnackbar(`Daily closing for ${format(daySummary.date, 'MMMM d, yyyy')} reversed successfully`, { variant: 'success' });
      
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error reversing daily closing:', error);
      enqueueSnackbar('Failed to reverse daily closing', { variant: 'error' });
      throw error; // Re-throw to propagate to the dialog
    } finally {
      setIsReversingClosing(false);
    }
  };

  const [open, setOpen] = useState(false);
  
  const paymentMethodDisplayNames: { [key: string]: string } = {
    'cash': 'Cash',
    'card': 'Credit/Debit Card',
    'qr': 'QR Payment',
    'bank_transfer': 'Bank Transfer',
    'other': 'Other'
  };

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          backgroundColor: open ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
          borderLeft: open ? '4px solid #1976d2' : 'none'
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {format(daySummary.date, 'dd MMM yyyy')}
          {isToday(daySummary.date) && (
            <Chip 
              label="Today" 
              size="small" 
              color="primary" 
              variant="outlined" 
              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </TableCell>
        <TableCell>
          {daySummary.transactionCount}
          {daySummary.voidedCount && daySummary.voidedCount > 0 && (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              ({daySummary.transactionCount + daySummary.voidedCount} total)
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">{formatCurrency(daySummary.totalSales)}</TableCell>
        <TableCell 
          align="center" 
          sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderLeft: '2px solid rgba(224, 224, 224, 1)'
          }}
        >
          {formatCurrency(daySummary.paymentMethods['cash'] || 0)}
        </TableCell>
        <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          {formatCurrency(daySummary.paymentMethods['card'] || 0)}
        </TableCell>
        <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          {formatCurrency(daySummary.paymentMethods['qr'] || 0)}
        </TableCell>
        <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          {formatCurrency(daySummary.paymentMethods['bank_transfer'] || 0)}
        </TableCell>
        <TableCell 
          align="center" 
          sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRight: '1px solid rgba(224, 224, 224, 1)'
          }}
        >
          {formatCurrency(daySummary.paymentMethods['other'] || 0)}
        </TableCell>
        <TableCell 
          align="center"
          sx={{ 
            borderLeft: '1px solid rgba(224, 224, 224, 1)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Chip 
              size="small" 
              icon={daySummary.isClosed ? <CheckCircleIcon /> : <ErrorIcon />}
              label={daySummary.isClosed ? 'Closed' : 'Open'}
              color={daySummary.isClosed ? 'success' : 'warning'}
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
            <Chip 
              size="small" 
              icon={daySummary.glTransactionId ? <CheckCircleIcon /> : <ErrorIcon />}
              label={daySummary.glTransactionId ? 'GL Posted' : 'No GL'}
              color={daySummary.glTransactionId ? 'success' : 'default'}
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </TableCell>
        <TableCell 
          align="right"
          sx={{ 
            borderLeft: '1px solid rgba(224, 224, 224, 1)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Reverse Closing Button - only show if day is closed and has GL entries */}
            {daySummary.isClosed && daySummary.glTransactionId && (
              <Button
                size="small"
                onClick={handleOpenReverseDialog}
                disabled={isReversingClosing}
                startIcon={<UndoIcon fontSize="small" />}
                sx={{
                  bgcolor: '#9e9e9e',
                  color: 'white',
                  borderRadius: '20px',
                  textTransform: 'uppercase',
                  px: 2,
                  '&:hover': {
                    bgcolor: '#757575'
                  }
                }}
              >
                REVERSE
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(daySummary.date);
              }}
              sx={{ py: 0.5 }}
            >
              DETAILS
            </Button>
          </Box>
        </TableCell>
      </TableRow>
      
      {/* Reverse GL Entry Dialog */}
      {daySummary.glTransactionId && (
        <ReverseGLEntryDialog
          open={reverseDialogOpen}
          onClose={() => setReverseDialogOpen(false)}
          onConfirm={handleReverseClosing}
          date={daySummary.date}
          glTransactionId={daySummary.glTransactionId}
        />
      )}
      
      <TableRow>
        <TableCell 
          style={{ paddingBottom: 0, paddingTop: 0 }} 
          colSpan={12} 
          sx={{ backgroundColor: 'rgba(25, 118, 210, 0.04)' }}
        >
          <Box sx={{ display: open ? 'block' : 'none' }}>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Category Breakdown */}
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>By Category</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(daySummary.categories).map(([category, amount]) => (
                          <TableRow key={category}>
                            <TableCell>{category}</TableCell>
                            <TableCell align="right">{formatCurrency(amount)}</TableCell>
                            <TableCell align="right">
                              {daySummary.totalSales > 0 ? `${((amount / daySummary.totalSales) * 100).toFixed(1)}%` : '0%'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {Object.keys(daySummary.categories).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No sales data</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                
                {/* Payment Method Breakdown */}
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>By Payment Method</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Method</TableCell>
                          {['cash', 'card', 'qr', 'bank_transfer', 'other'].map((method) => (
                            <TableCell key={method} align="center">{paymentMethodDisplayNames[method]}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Amount</TableCell>
                          {['cash', 'card', 'qr', 'bank_transfer', 'other'].map((method) => {
                            const amount = daySummary.paymentMethods[method] || 0;
                            return (
                              <TableCell key={method} align="center">{formatCurrency(amount)}</TableCell>
                            );
                          })}
                        </TableRow>
                        <TableRow>
                          <TableCell>%</TableCell>
                          {['cash', 'card', 'qr', 'bank_transfer', 'other'].map((method) => {
                            const amount = daySummary.paymentMethods[method] || 0;
                            return (
                              <TableCell key={method} align="center">
                                {daySummary.totalSales > 0 ? `${((amount / daySummary.totalSales) * 100).toFixed(1)}%` : '0%'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            </Box>
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};

const MonthlyClosing: React.FC = () => {
  const currentYear = getYear(new Date());
  const currentMonth = getMonth(new Date());
  
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dailySummaries, setDailySummaries] = useState<DailySummary[]>([]);
  const [monthlyTotals, setMonthlyTotals] = useState<MonthlyTotals>({totalSales: 0, categories: {}, paymentMethods: {}});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(31); // Show all days by default
  
  // Commission GL entry functionality has been moved to the Commission Breakdown page
  
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  // Generate years for dropdown (5 years back, 2 years forward)
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  
  // Month names for dropdown
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchMonthData(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const fetchMonthData = async (year: number, month: number) => {
    setLoading(true);
    setError(null);
    
    try {
      // Create date range for the selected month
      const startDate = startOfMonth(new Date(year, month));
      const endDate = endOfMonth(new Date(year, month));
      
      // Get all days in the month
      const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });
      
      // Get sales data from Firebase for the entire month
      const salesData = await getSales(startDate, endDate);
      
      // Get daily closing data for the month
      const dailyClosings = await getDailyClosingsForMonth(year, month);
      
      // Create maps for closing status and GL transaction ID
      const closingStatusMap = new Map<string, boolean>();
      const glTransactionMap = new Map<string, string | undefined>();
      
      dailyClosings.forEach(closing => {
        const closingDate = closing.date instanceof Date ? 
          closing.date : 
          new Date((closing.date as Timestamp).seconds * 1000);
        const dateStr = format(closingDate, 'yyyy-MM-dd');
        closingStatusMap.set(dateStr, closing.isClosed);
        glTransactionMap.set(dateStr, closing.glTransactionId);
      });
      
      // Transform the sales data
      const transformedSales = salesData.map(sale => {
        return {
          ...sale,
          createdAt: sale.createdAt || Timestamp.now(),
          status: (sale.status as any) || 'active'
        } as Sale;
      });
      
      // Create daily summaries
      const summaries: DailySummary[] = daysInMonth.map(day => {
        // Filter sales for this day
        const daySales = transformedSales.filter(sale => {
          // Handle Firestore Timestamp or regular Date
          const saleDate = sale.createdAt instanceof Timestamp ? 
            sale.createdAt.toDate() : 
            new Date(sale.createdAt as Date);
          return isSameDay(saleDate, day);
        });
        
        // Filter out voided transactions for calculations
        const validSales = daySales.filter(sale => sale.status !== 'voided');
        
        // Calculate total sales
        const totalSales = validSales.reduce((sum, sale) => sum + sale.total, 0);

        // Calculate category totals with proportional discount
        const categories: {[key: string]: number} = {};
        validSales.forEach(sale => {
          // Calculate subtotal for this sale from items
          const saleSubtotal = sale.items.reduce((sum, item) => sum + item.total, 0);

          // Calculate category subtotals for this sale
          const saleCategoryTotals: {[key: string]: number} = {};
          sale.items.forEach(item => {
            const category = item.type === 'product' ? 'Products' : 'Services';
            saleCategoryTotals[category] = (saleCategoryTotals[category] || 0) + item.total;
          });

          // Apply proportional discount if exists
          const saleDiscount = sale.discount || 0;
          if (saleDiscount > 0 && saleSubtotal > 0) {
            // Distribute discount proportionally across categories
            Object.entries(saleCategoryTotals).forEach(([category, subtotal]) => {
              const categoryDiscountAmount = (subtotal / saleSubtotal) * saleDiscount;
              const categoryTotal = subtotal - categoryDiscountAmount;
              categories[category] = (categories[category] || 0) + categoryTotal;
            });
          } else {
            // No discount, add full amounts
            Object.entries(saleCategoryTotals).forEach(([category, subtotal]) => {
              categories[category] = (categories[category] || 0) + subtotal;
            });
          }
        });
        
        // Calculate payment method totals
        const paymentMethods: {[key: string]: number} = {};
        validSales.forEach(sale => {
          const method = sale.paymentMethod;
          paymentMethods[method] = (paymentMethods[method] || 0) + sale.total;
        });
        
        // Check if day is closed and has GL transaction from the maps
        const dateStr = format(day, 'yyyy-MM-dd');
        const isClosed = closingStatusMap.has(dateStr) ? 
          closingStatusMap.get(dateStr)! : 
          false; // Default to not closed if no record exists
          
        // Get GL transaction ID if it exists
        const glTransactionId = glTransactionMap.has(dateStr) ?
          glTransactionMap.get(dateStr) :
          undefined; // Default to undefined if no record exists
        
        return {
          date: day,
          totalSales,
          transactionCount: validSales.length,
          voidedCount: daySales.length - validSales.length,
          categories,
          paymentMethods,
          isClosed,
          glTransactionId
        };
      });
      
      setDailySummaries(summaries);
      
      // Calculate monthly totals
      calculateMonthlyTotals(summaries);
    } catch (err) {
      console.error('Error fetching monthly data:', err);
      setError('Failed to load monthly closing data. Please try again.');
      enqueueSnackbar('Failed to load monthly closing data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };
  
  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(event.target.value));
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleViewDetails = (date: Date) => {
    // Navigate to the daily closing page with the selected date
    navigate(`/daily-closing?date=${format(date, 'yyyy-MM-dd')}`);
  };
  
  // Calculate monthly totals
  const calculateMonthlyTotals = (summaries: DailySummary[]) => {
    const totals: MonthlyTotals = {
      totalSales: 0,
      categories: {},
      paymentMethods: {}
    };
    
    summaries.forEach(summary => {
      // Add to total sales
      totals.totalSales += summary.totalSales;
      
      // Add to category totals
      Object.entries(summary.categories).forEach(([category, amount]) => {
        totals.categories[category] = (totals.categories[category] || 0) + amount;
      });
      
      // Add to payment method totals
      Object.entries(summary.paymentMethods).forEach(([method, amount]) => {
        totals.paymentMethods[method] = (totals.paymentMethods[method] || 0) + amount;
      });
    });
    
    setMonthlyTotals(totals);
  };
  
  // Commission GL entry functionality has been moved to the Commission Breakdown page
  
  // Calculate monthly totals by payment method
  const calculateMonthlyTotalsByMethod = () => {
    const methodTotals: {[key: string]: number} = {
      'cash': 0,
      'card': 0,
      'qr': 0,
      'bank_transfer': 0,
      'other': 0
    };
    
    dailySummaries.forEach(day => {
      Object.entries(day.paymentMethods).forEach(([method, amount]) => {
        methodTotals[method] = (methodTotals[method] || 0) + amount;
      });
    });
    
    return methodTotals;
  };
  
  const monthlyMethodTotals = calculateMonthlyTotalsByMethod();
  
  // Calculate monthly total
  const monthlyTotal = dailySummaries.reduce((sum, day) => sum + day.totalSales, 0);
  const monthlyTransactions = dailySummaries.reduce((sum, day) => sum + day.transactionCount, 0);
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Month/Year Selection */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Monthly Totals
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              {months.map((month, index) => (
                <MenuItem key={month} value={index}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      {/* Monthly Summary Card */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Sales for {months[selectedMonth]} {selectedYear}</Typography>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
              {formatCurrency(monthlyTotal)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
            <Typography variant="h6">{monthlyTransactions}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Days Closed</Typography>
            <Typography variant="h6">
              {dailySummaries.filter(day => day.isClosed).length} / {dailySummaries.length}
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table stickyHeader aria-label="collapsible table" size="small">
              <TableHead>
                <TableRow sx={{ '& th': { borderBottom: '2px solid rgba(224, 224, 224, 1)' } }}>
                  <TableCell width="50px" />
                  <TableCell>Date</TableCell>
                  <TableCell>Transactions</TableCell>
                  <TableCell align="right">Total</TableCell>
                  {/* Payment Method Group - with different background */}
                  <TableCell 
                    align="center" 
                    colSpan={5} 
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderLeft: '2px solid rgba(224, 224, 224, 1)',
                      borderRight: '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    Payment Methods
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      borderLeft: '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      borderLeft: '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="50px" />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  {/* Payment Method Sub-headers */}
                  <TableCell 
                    align="center" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderLeft: '2px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    Cash
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    Credit/Debit Card
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    QR Payment
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    Bank Transfer
                  </TableCell>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRight: '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    Other
                  </TableCell>
                  <TableCell sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }} />
                  <TableCell sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {dailySummaries
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((daySummary) => (
                    <DayRow 
                      key={format(daySummary.date, 'yyyy-MM-dd')} 
                      daySummary={daySummary} 
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                {dailySummaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">No data available for this month</TableCell>
                  </TableRow>
                )}
                
                {/* Subtotal row at the bottom */}
                {dailySummaries.length > 0 && (
                  <TableRow sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '& td': { fontWeight: 600, borderTop: '2px solid rgba(224, 224, 224, 1)' }
                  }}>
                    <TableCell width="50px" />
                    <TableCell>Monthly Total</TableCell>
                    <TableCell>{monthlyTransactions}</TableCell>
                    <TableCell align="right">{formatCurrency(monthlyTotal)}</TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                        borderLeft: '1px solid rgba(224, 224, 224, 1)'
                      }}
                    >
                      {formatCurrency(monthlyMethodTotals['cash'] || 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}>
                      {formatCurrency(monthlyMethodTotals['card'] || 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}>
                      {formatCurrency(monthlyMethodTotals['qr'] || 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}>
                      {formatCurrency(monthlyMethodTotals['bank_transfer'] || 0)}
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                        borderRight: '1px solid rgba(224, 224, 224, 1)'
                      }}
                    >
                      {formatCurrency(monthlyMethodTotals['other'] || 0)}
                    </TableCell>
                    <TableCell align="center" sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }}>
                      {dailySummaries.filter(day => day.isClosed).length} / {dailySummaries.length}
                    </TableCell>
                    <TableCell align="right" sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 15, 31]}
            component="div"
            count={dailySummaries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      {/* Commission GL entry functionality has been moved to the Commission Breakdown page */}
    </Box>
  );
};

export default MonthlyClosing;
